<template>
	<div class="code-breakdown">
		<h1>Project Breakdown</h1>
		<p class="code-breakdown__subtitle">Detailed breakdown of all components to be generated</p>

		<div class="code-breakdown__accordion">
			<Section
				v-for="{ icon, title, time, value, items } in sections"
				:key="title"
				:icon
				:time
				:title
				:value>
				<SectionItem
					v-for="{ label, count, per, minutes } in items"
					:key="label"
					:count
					:label
					:per
					:time="toTime(minutes)" />
			</Section>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Section from './CodeBreakdown/Section.vue'
import SectionItem from './CodeBreakdown/SectionItem.vue'
import type { AppMetricsComposable } from '../../composables/useAppMetrics'

const props = defineProps<{
	appMetrics: AppMetricsComposable
}>()

const sections = computed(() => [
	{
		icon: 'far fa-database',
		title: 'Database',
		time: toTime(props.appMetrics.totalDatabaseEstimate.value),
		value: formatCurrency(props.appMetrics.totalDatabaseValue.value),
		items: props.appMetrics.databaseItems.value,
	},
	{
		icon: 'far fa-code-simple',
		title: 'API',
		time: toTime(props.appMetrics.totalApiEstimate.value),
		value: formatCurrency(props.appMetrics.totalApiValue.value),
		items: props.appMetrics.apiItems.value,
	},
	{
		icon: 'far fa-palette',
		title: 'UI Components',
		time: toTime(props.appMetrics.totalUiEstimate.value),
		value: formatCurrency(props.appMetrics.totalUiValue.value),
		items: props.appMetrics.uiItems.value,
	},
	{
		icon: 'far fa-gear',
		title: 'Dev Environment',
		time: toTime(props.appMetrics.totalDevEnvironmentEstimate.value),
		value: formatCurrency(props.appMetrics.totalDevEnvironmentValue.value),
		items: props.appMetrics.devEnvItems.value,
	},
])

// Formatters
function formatCurrency(val: string | number): string {
	return `€${Number(val).toFixed(2)}`
}
function toTime(minutes: number) {
	return {
		hours: Math.floor(minutes / 60),
		minutes: minutes % 60,
	}
}
</script>

<style scoped lang="scss">
.code-breakdown {
	--card-bg: var(--p-surface-0);
	--border: var(--p-surface-200);
	--muted: var(--p-text-color-secondary);
	--chip-bg: var(--p-surface-100);

	@media all {
		--card-bg: var(--p-surface-900);
		--border: var(--p-surface-800);
		--muted: var(--p-surface-400);
		--chip-bg: var(--p-surface-800);
	}

	padding: 16px;
	background: var(--card-bg);
	border-radius: 8px;
	border: 1px solid var(--border);

	h1 {
		font-size: 28px;
		font-weight: 700;
		line-height: 1;
	}

	.code-breakdown__subtitle {
		margin: 6px 0 16px 0;
		color: var(--muted);
	}

	.code-breakdown__accordion {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}
}
</style>
